import { useCallback, useState } from 'react';

import ToastStatusGlobal from '@/components/toastStatusGlobal';
import { TEMPLATE_TYPE } from '@/shared/entities/enums/templateType';
import FilesService from '@/shared/services/filesService';
import { downloadFileLink } from '@/utils/common';

interface UseTemplateDownload {
  downloading: boolean;
  downloadTemplate: () => Promise<boolean>;
}

const filesService = new FilesService();

/**
 * Hook compartilhado para baixar um template oficial via o endpoint autenticado
 * `GET /templates/:type/download` do serviço sov-v2-files.
 *
 * - Trata a resposta como binária (Blob) e dispara o download no navegador.
 * - Usa o nome de arquivo vindo do header `Content-Disposition`, com fallback
 *   para o `fallbackFileName` informado.
 * - Em caso de falha, exibe um toast de erro e resolve como `false`. O 401 é
 *   tratado globalmente pelo interceptor de auth (modal-login).
 *
 * @param type Slug do template (fixo por tela, nunca vindo de input do cliente).
 * @param fallbackFileName Nome usado quando o header não estiver acessível.
 */
const useTemplateDownload = (
  type: TEMPLATE_TYPE,
  fallbackFileName: string,
): UseTemplateDownload => {
  const { toast } = ToastStatusGlobal();
  const [downloading, setDownloading] = useState<boolean>(false);

  const downloadTemplate = useCallback(async (): Promise<boolean> => {
    setDownloading(true);

    try {
      const { blob, fileName } = await filesService.downloadTemplate(type, fallbackFileName);

      const blobUrl = URL.createObjectURL(blob);
      downloadFileLink(blobUrl, fileName ?? fallbackFileName);
      URL.revokeObjectURL(blobUrl);

      return true;
    } catch (error) {
      console.error(error);
      toast('Oops!', 'Falha ao baixar arquivo.', 'error');

      return false;
    } finally {
      setDownloading(false);
    }
  }, [type, fallbackFileName, toast]);

  return {
    downloading,
    downloadTemplate,
  };
};

export default useTemplateDownload;
